import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import TambaLogo from "@/components/TambaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import SERVICES_CONFIG from "@/config/servicesConfig";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  AlertTriangle,
  AlertCircle,
  Info,
  Sparkles,
  ClipboardList,
  BarChart3,
  Brain,
  ListChecks,
  Lock,
  Play,
  Plus,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Helper: format metric values ---
function formatMetric(value, format, metrics) {
  if (value === undefined || value === null) return "—";
  if (format === "currency") return `$${Number(value).toLocaleString()}`;
  if (format === "percent") return `${value}%`;
  if (format === "years") return `${value} yrs`;
  if (format === "boolean") return value ? "Yes" : "No";
  if (format === "number") return `${value}`;
  if (format === "text") return `${value}`;
  if (format?.startsWith("fraction:")) {
    const totalKey = format.split(":")[1];
    return `${value} / ${metrics?.[totalKey] ?? "?"}`;
  }
  return `${value}`;
}

// --- Score Ring Component ---
const ScoreRing = ({ score, status, size = 120 }) => {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const colors = { critical: "#ef4444", needs_attention: "#f59e0b", good: "#3b82f6", excellent: "#10b981" };
  const color = colors[status] || "#6b7280";
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="currentColor" className="text-muted/30" strokeWidth={8} fill="none" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius} stroke={color} strokeWidth={8} fill="none"
          strokeLinecap="round" strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-2xl font-bold" style={{ color }}>{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{status?.replace("_", " ")}</span>
      </div>
    </div>
  );
};

// --- Priority Badge ---
const PriorityBadge = ({ priority }) => {
  const map = {
    critical: { bg: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300", icon: AlertCircle },
    high: { bg: "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300", icon: AlertTriangle },
    medium: { bg: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300", icon: Info },
    low: { bg: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300", icon: Check },
    completed: { bg: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300", icon: Check },
  };
  const cfg = map[priority] || map.medium;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg}`}>
      <Icon className="w-3 h-3" /> {priority}
    </span>
  );
};

// ===== MAIN PAGE =====
const ServiceDetailPage = () => {
  const { serviceType } = useParams();
  const navigate = useNavigate();
  const config = SERVICES_CONFIG[serviceType];

  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [answers, setAnswers] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [aiRec, setAiRec] = useState("");
  const [ruleRecs, setRuleRecs] = useState([]);
  const [actionPlan, setActionPlan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recLoading, setRecLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("tamba_user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  // Load saved assessment or demo
  const loadSavedOrDemo = useCallback(async () => {
    if (user) {
      try {
        const res = await axios.get(`${API}/services/assessment/${user.id}/${serviceType}`);
        if (res.data.success && res.data.assessment) {
          setAnswers(res.data.assessment.answers || {});
          setAnalysis(res.data.assessment.analysis || null);
          setIsDemo(false);
          return;
        }
      } catch {}
    }
    // Load demo
    try {
      const res = await axios.get(`${API}/services/demo/${serviceType}`);
      if (res.data.success) {
        setAnswers(res.data.answers);
        setAnalysis(res.data.analysis);
        setIsDemo(true);
      }
    } catch {}
  }, [user, serviceType]);

  useEffect(() => { loadSavedOrDemo(); }, [loadSavedOrDemo]);

  if (!config) {
    return <div className="min-h-screen flex items-center justify-center"><p>Service not found</p></div>;
  }

  const handleSubmitAssessment = async () => {
    if (!user) {
      toast.error("Please log in to save your assessment.");
      navigate("/auth");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/services/assess`, { service_type: serviceType, user_id: user.id, answers });
      if (res.data.success) {
        setAnalysis(res.data.analysis);
        setIsDemo(false);
        setActiveTab("analysis");
        toast.success("Assessment complete!");
      }
    } catch (err) {
      toast.error("Assessment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetRecommendations = async () => {
    const userId = user?.id || "demo";
    setRecLoading(true);
    try {
      const res = await axios.post(`${API}/services/recommend`, {
        service_type: serviceType, user_id: userId,
        assessment_data: analysis,
      });
      if (res.data.success) {
        setAiRec(res.data.ai_recommendation || "");
        setRuleRecs(res.data.rule_recommendations || []);
        setActionPlan(res.data.action_plan || []);
        setActiveTab("recommendations");
      }
    } catch {
      toast.error("Could not load recommendations.");
    } finally {
      setRecLoading(false);
    }
  };

  // --- Form field helpers ---
  const updateAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const updateDebtField = (index, field, value) => {
    const debts = [...(answers.debts || [])];
    debts[index] = { ...debts[index], [field]: value };
    setAnswers((prev) => ({ ...prev, debts }));
  };
  const addDebt = () => setAnswers((prev) => ({ ...prev, debts: [...(prev.debts || []), { name: "", type: "credit_card", balance: "", rate: "", min_payment: "" }] }));
  const removeDebt = (i) => setAnswers((prev) => ({ ...prev, debts: (prev.debts || []).filter((_, idx) => idx !== i) }));

  const toggleMultiselect = (key, val) => {
    const arr = answers[key] || [];
    updateAnswer(key, arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: ClipboardList },
    { key: "assessment", label: "Assessment", icon: ClipboardList },
    { key: "analysis", label: "Analysis", icon: BarChart3, disabled: !analysis },
    { key: "recommendations", label: "AI Advice", icon: Brain, disabled: !analysis },
    { key: "action-plan", label: "Action Plan", icon: ListChecks, disabled: !analysis },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid={`service-detail-${serviceType}`}>
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-background/90 backdrop-blur-xl border-b border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")}><TambaLogo className="h-10 w-auto" /></button>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            {!user ? (
              <Button onClick={() => navigate("/auth")} className="rounded-full px-5 bg-tamba-blue hover:bg-tamba-blue/90" data-testid="service-login-btn">
                Log In
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="service-dashboard-btn">Dashboard</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-6 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate("/services")} className="mb-4 text-muted-foreground" data-testid="service-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> All Services
          </Button>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl ${config.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
              <ClipboardList className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">{config.title}</h1>
              <p className={`text-sm font-medium ${config.accent}`}>{config.tagline}</p>
              <p className="text-sm text-muted-foreground mt-1">{config.description}</p>
            </div>
          </div>

          {isDemo && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-center gap-3">
              <Play className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Demo Mode — showing sample data</p>
                <p className="text-xs text-amber-600 dark:text-amber-400">Log in and complete your own assessment for personalized results</p>
              </div>
              {!user && <Button size="sm" onClick={() => navigate("/auth")} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="demo-login-cta">Log In</Button>}
            </motion.div>
          )}
        </div>
      </section>

      {/* Tabs */}
      <section className="px-4 sm:px-6 sticky top-[60px] z-40 bg-background/90 backdrop-blur-xl border-b border-border/20">
        <div className="max-w-5xl mx-auto flex gap-1 overflow-x-auto py-2 no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => !tab.disabled && setActiveTab(tab.key)}
              disabled={tab.disabled}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.key ? `${config.color} text-white shadow-md` : tab.disabled ? "text-muted-foreground/40 cursor-not-allowed" : "text-muted-foreground hover:bg-secondary"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.disabled && <Lock className="w-3 h-3 ml-0.5" />}
            </button>
          ))}
        </div>
      </section>

      {/* Tab Content */}
      <section className="px-4 sm:px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            {/* === OVERVIEW === */}
            {activeTab === "overview" && (
              <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="border-border/50">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-bold mb-3">Your Journey</h3>
                      <div className="space-y-4">
                        {[
                          { step: 1, title: "Complete Assessment", desc: "Answer a few questions about your current situation" },
                          { step: 2, title: "View Analysis", desc: "See your personalized score and key metrics" },
                          { step: 3, title: "Get AI Recommendations", desc: "Receive tailored advice from Tamba AI" },
                          { step: 4, title: "Follow Action Plan", desc: "Step-by-step roadmap to improve your finances" },
                        ].map((s) => (
                          <div key={s.step} className="flex gap-3">
                            <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>{s.step}</div>
                            <div>
                              <p className="font-semibold text-sm">{s.title}</p>
                              <p className="text-xs text-muted-foreground">{s.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button onClick={() => setActiveTab("assessment")} className={`mt-6 w-full rounded-full ${config.color} hover:opacity-90 text-white`} data-testid="start-assessment-btn">
                        Start Assessment <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Quick preview from demo/saved */}
                  {analysis && (
                    <Card className={`border ${config.ring}`}>
                      <CardContent className="p-6">
                        <h3 className="font-heading text-lg font-bold mb-4">{isDemo ? "Sample" : "Your"} Score</h3>
                        <div className="flex items-center gap-6">
                          <ScoreRing score={analysis.score} status={analysis.status} />
                          <div className="flex-1 space-y-2">
                            {Object.entries(config.metricLabels || {}).slice(0, 4).map(([key, label]) => (
                              <div key={key} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{label}</span>
                                <span className="font-medium">{formatMetric(analysis.metrics?.[key], config.metricFormats?.[key], analysis.metrics)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <Button variant="outline" onClick={() => setActiveTab("analysis")} className="mt-4 w-full rounded-full" data-testid="view-analysis-btn">
                          View Full Analysis
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </motion.div>
            )}

            {/* === ASSESSMENT === */}
            {activeTab === "assessment" && (
              <motion.div key="assessment" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-bold mb-1">{config.calculatorTitle || "Assessment"}</h3>
                    <p className="text-sm text-muted-foreground mb-6">{config.calculatorDescription || "Tell us about your current situation"}</p>
                    <div className="space-y-5">
                      {config.assessmentQuestions.map((q) => {
                        if (q.showIf && !answers[q.showIf]) return null;
                        if (q.type === "debt_list") {
                          return (
                            <div key={q.key} data-testid={`field-${q.key}`}>
                              <label className="block text-sm font-semibold mb-2">{q.label}</label>
                              {(answers.debts || []).map((debt, i) => (
                                <div key={i} className="border rounded-xl p-4 mb-3 bg-secondary/30 space-y-3">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs font-bold text-muted-foreground">DEBT {i + 1}</span>
                                    <button onClick={() => removeDebt(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {q.fields.map((f) => (
                                      <div key={f.key}>
                                        <label className="text-xs text-muted-foreground">{f.label}</label>
                                        {f.type === "select" ? (
                                          <select value={debt[f.key] || ""} onChange={(e) => updateDebtField(i, f.key, e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-background">
                                            {f.options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
                                          </select>
                                        ) : (
                                          <input type={f.type} value={debt[f.key] || ""} onChange={(e) => updateDebtField(i, f.key, e.target.value)} placeholder={f.placeholder} className="w-full border rounded-lg px-3 py-2 text-sm bg-background" />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              <Button variant="outline" size="sm" onClick={addDebt} className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Add Debt</Button>
                            </div>
                          );
                        }
                        if (q.type === "toggle") {
                          return (
                            <div key={q.key} className="flex items-center justify-between" data-testid={`field-${q.key}`}>
                              <label className="text-sm font-medium">{q.label}</label>
                              <Switch checked={!!answers[q.key]} onCheckedChange={(v) => updateAnswer(q.key, v)} />
                            </div>
                          );
                        }
                        if (q.type === "slider") {
                          return (
                            <div key={q.key} data-testid={`field-${q.key}`}>
                              <label className="text-sm font-medium">{q.label}: <span className={`font-bold ${config.accent}`}>{answers[q.key] ?? q.min}</span></label>
                              <Slider min={q.min} max={q.max} step={q.step} value={[answers[q.key] ?? q.min]} onValueChange={([v]) => updateAnswer(q.key, v)} className="mt-2" />
                            </div>
                          );
                        }
                        if (q.type === "select") {
                          return (
                            <div key={q.key} data-testid={`field-${q.key}`}>
                              <label className="block text-sm font-medium mb-1">{q.label}</label>
                              <select value={answers[q.key] || ""} onChange={(e) => updateAnswer(q.key, e.target.value)} className="w-full border rounded-lg px-3 py-2.5 text-sm bg-background">
                                <option value="">Select...</option>
                                {q.options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </div>
                          );
                        }
                        if (q.type === "multiselect") {
                          return (
                            <div key={q.key} data-testid={`field-${q.key}`}>
                              <label className="block text-sm font-medium mb-2">{q.label}</label>
                              <div className="flex flex-wrap gap-2">
                                {q.options.map((o) => {
                                  const val = typeof o === "string" ? o : o.value;
                                  const label = typeof o === "string" ? o : o.label;
                                  const selected = (answers[q.key] || []).includes(val);
                                  return (
                                    <button key={val} onClick={() => toggleMultiselect(q.key, val)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${selected ? `${config.color} text-white border-transparent` : "border-border text-muted-foreground hover:border-foreground/30"}`}>
                                      {selected && <Check className="w-3 h-3 inline mr-1" />}{label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={q.key} data-testid={`field-${q.key}`}>
                            <label className="block text-sm font-medium mb-1">{q.label}</label>
                            <input type="number" value={answers[q.key] || ""} onChange={(e) => updateAnswer(q.key, e.target.value)} placeholder={q.placeholder} className="w-full border rounded-lg px-3 py-2.5 text-sm bg-background" />
                          </div>
                        );
                      })}
                    </div>
                    <Button onClick={handleSubmitAssessment} disabled={loading} className={`mt-6 w-full rounded-full py-6 ${config.color} hover:opacity-90 text-white font-semibold`} data-testid="submit-assessment-btn">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                      {loading ? "Analyzing..." : user ? "Get My Analysis" : "Log In to Save Results"}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* === ANALYSIS === */}
            {activeTab === "analysis" && analysis && (
              <motion.div key="analysis" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Score */}
                  <Card className={`border ${config.ring}`}>
                    <CardContent className="p-6 flex flex-col items-center">
                      <h3 className="font-heading text-lg font-bold mb-4">{isDemo ? "Sample" : "Your"} Score</h3>
                      <ScoreRing score={analysis.score} status={analysis.status} size={140} />
                      <p className="mt-3 text-sm text-muted-foreground text-center capitalize">{analysis.status?.replace("_", " ")}</p>
                    </CardContent>
                  </Card>

                  {/* Metrics */}
                  <Card className="md:col-span-2">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-bold mb-4">Key Metrics</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(config.metricLabels || {}).map(([key, label]) => (
                          <div key={key} className="bg-secondary/40 rounded-xl p-3">
                            <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                            <p className="text-lg font-bold">{formatMetric(analysis.metrics?.[key], config.metricFormats?.[key], analysis.metrics)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Rule-based findings */}
                {analysis.rules?.length > 0 && (
                  <Card className="mt-6">
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-bold mb-4">Key Findings</h3>
                      <div className="space-y-3">
                        {analysis.rules.map((r, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-secondary/30">
                            <PriorityBadge priority={r.priority} />
                            <div>
                              <p className="font-semibold text-sm">{r.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{r.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="mt-6 flex justify-center">
                  <Button onClick={handleGetRecommendations} disabled={recLoading} className={`rounded-full px-8 py-6 ${config.color} hover:opacity-90 text-white font-semibold`} data-testid="get-recommendations-btn">
                    {recLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Sparkles className="w-5 h-5 mr-2" />}
                    {recLoading ? "Generating AI Advice..." : "Get Personalized Recommendations"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* === RECOMMENDATIONS === */}
            {activeTab === "recommendations" && (
              <motion.div key="recs" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="space-y-6">
                  {/* AI Recommendation */}
                  {aiRec && (
                    <Card className={`border ${config.ring} overflow-hidden`}>
                      <div className={`${config.color} px-6 py-3 flex items-center gap-2 text-white`}>
                        <Sparkles className="w-5 h-5" />
                        <span className="font-semibold">Tamba AI Recommendation</span>
                      </div>
                      <CardContent className="p-6">
                        <div className="prose dark:prose-invert prose-sm max-w-none whitespace-pre-wrap">{aiRec}</div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Rule-based */}
                  {ruleRecs.length > 0 && (
                    <Card>
                      <CardContent className="p-6">
                        <h3 className="font-heading text-lg font-bold mb-4">Key Recommendations</h3>
                        <div className="space-y-3">
                          {ruleRecs.map((r, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-secondary/30">
                              <PriorityBadge priority={r.priority} />
                              <div>
                                <p className="font-semibold text-sm">{r.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{r.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-center">
                    <Button variant="outline" onClick={() => setActiveTab("action-plan")} className="rounded-full px-6" data-testid="view-action-plan-btn">
                      View Action Plan <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* === ACTION PLAN === */}
            {activeTab === "action-plan" && (
              <motion.div key="plan" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-bold mb-6">Your Action Plan</h3>
                    <div className="space-y-0">
                      {(actionPlan.length > 0 ? actionPlan : analysis?.action_plan || []).map((step, i, arr) => (
                        <div key={i} className="flex gap-4">
                          {/* Timeline */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full ${step.priority === "completed" ? "bg-emerald-500" : config.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                              {step.priority === "completed" ? <Check className="w-5 h-5" /> : step.step || i + 1}
                            </div>
                            {i < arr.length - 1 && <div className="w-0.5 h-full min-h-[40px] bg-border" />}
                          </div>
                          {/* Content */}
                          <div className="pb-8">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-sm">{step.title}</h4>
                              <PriorityBadge priority={step.priority} />
                            </div>
                            <p className="text-sm text-muted-foreground">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {!user && (
                  <div className="mt-6 text-center">
                    <Card className={`border ${config.ring} inline-block`}>
                      <CardContent className="p-6">
                        <Lock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="font-semibold mb-1">Save Your Progress</p>
                        <p className="text-sm text-muted-foreground mb-4">Log in to save your assessment and track your action plan over time.</p>
                        <Button onClick={() => navigate("/auth")} className={`rounded-full ${config.color} text-white`} data-testid="action-plan-login-btn">
                          Get Started <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
